import { STORAGE_CONST } from '@common/constants'
import { BuySavedSessionState } from '@buy/state/checkout/state'
import { OrderSummaryLine } from '@buy/state/order-summary/order-summary'
import { get, isEmpty, isNull, set } from 'lodash-es'

export const loadSessionState = () => {
    try {
        const serializedState = sessionStorage.getItem(STORAGE_CONST.GTC_COUPON_STORE)
        const language = sessionStorage.getItem(STORAGE_CONST.SAVED_LANGUAGE)
        if (serializedState === null && language === null) {
            return undefined
        }

        return {
            ...(serializedState && { coupon: JSON.parse(serializedState) }),
            ...(language && { language: JSON.parse(language) }),
        } as BuySavedSessionState
    } catch (err) {
        return undefined
    }
}

export const saveSessionState = (state: BuySavedSessionState) => {
    try {
        const serializedState = JSON.stringify(state)
        sessionStorage.setItem(STORAGE_CONST.GTC_COUPON_STORE, serializedState)
    } catch (err) {
        // fail silently
    }
}

export const clearSessionState = () => {
    try {
        const coupon = sessionStorage.getItem(STORAGE_CONST.GTC_COUPON_STORE)
        const language = sessionStorage.getItem(STORAGE_CONST.SAVED_LANGUAGE)

        if (coupon) sessionStorage.removeItem(STORAGE_CONST.GTC_COUPON_STORE)
        if (language) sessionStorage.removeItem(STORAGE_CONST.SAVED_LANGUAGE)
    } catch (err) {
        // fail silently
    }
}

const getStore = () => {
    const buySessionStore = sessionStorage.getItem(STORAGE_CONST.BUY_CHECKOUT_STORE)
    const parsedStore: BuyFlexibleStoreState = !isNull(buySessionStore) ? JSON.parse(buySessionStore) : {}
    return parsedStore
}

export const setCheckoutSessionState = (
    data: OrderSummaryLine | OrderSummaryLine[] | string | undefined,
    key: string,
    product: string,
    plan: string
) => {
    const parsedStore = getStore()
    set(parsedStore, `${product}.${plan}.${key}`, data)
    sessionStorage.setItem(STORAGE_CONST.BUY_CHECKOUT_STORE, JSON.stringify(parsedStore))
}

export const createOrUpdateCheckoutSessionState = (
    data: Record<string, number | string | boolean> | string[],
    key: string,
    product: string,
    plan: string
) => {
    const parsedStore = getStore()
    const existingData = get(parsedStore, `${product}.${plan}.${key}`)
    if (!isEmpty(existingData)) {
        const newData = { ...existingData, ...data }
        set(parsedStore, `${product}.${plan}.${key}`, newData)
    } else set(parsedStore, `${product}.${plan}.${key}`, data)
    sessionStorage.setItem(STORAGE_CONST.BUY_CHECKOUT_STORE, JSON.stringify(parsedStore))
}

export const getCheckoutSessionState = (product: string, plan: string) => {
    const parsedStore = getStore()
    if (isEmpty(get(parsedStore, `${product}.${plan}`))) return undefined
    return parsedStore[product]![plan]!
}

export const clearCheckoutSessionState = (product: string, plan?: string, lineType?: string) => {
    const parsedStore = getStore()
    if (isEmpty(plan)) {
        set(parsedStore, `${product}`, undefined)
    } else if (isEmpty(lineType)) {
        set(parsedStore, `${product}.${plan}`, undefined)
    } else {
        set(parsedStore, `${product}.${plan}.${lineType}`, undefined)
    }
    sessionStorage.setItem(STORAGE_CONST.BUY_CHECKOUT_STORE, JSON.stringify(parsedStore))
}

export const SessionKeys = {
    baseLine: 'baseLine',
    additonalLines: 'additonalLines',
    addonLines: 'addonLines',
    volumeFields: 'volumeFields',
    addonFields: 'addonFields',
    billingTenure: 'billingTenure',
}

interface BuyFlexibleStoreState {
    [key: string]: Record<string, SessionFieldData> | undefined
}

export interface SessionFieldData {
    baseLine: OrderSummaryLine | undefined
    additonalLines: OrderSummaryLine[] | undefined
    addonLines: OrderSummaryLine[] | undefined
    volumeFields: Record<string, number | string>
    addonFields: Record<string, boolean>
    billingTenure: string
}
